'use client';

import * as React from 'react';
import { motion } from 'framer-motion';

import { cn } from '@/lib/utils';

export type PublicPageCTAProps = {
  /** Headline rendered as H2. Plain text or rich nodes. */
  title: React.ReactNode;
  /** Supporting paragraph below the headline. */
  description?: React.ReactNode;
  /** Action buttons rendered in a row (wraps on small screens). */
  actions?: React.ReactNode;
  /** Small reassurance / social-proof line rendered under the actions, inside the card. */
  footnote?: React.ReactNode;
  /** Render with the burgundy accented border + tinted background instead of the neutral card. */
  emphasized?: boolean;
  className?: string;
};

/**
 * Shared "final CTA" panel used at the bottom of marketing/public pages.
 * Replaces the bespoke `border border-primary border-t bg-primary/5 p-12 lg:p-16`
 * blocks pasted into about-us, how-it-works, etc., and aligns sizing with the
 * design system (page-level title, body-lg description).
 */
export function PublicPageCTA({
  title,
  description,
  actions,
  footnote,
  emphasized = true,
  className,
}: PublicPageCTAProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={cn(
        'border border-t p-8 sm:p-10 text-center',
        emphasized
          ? 'border-primary border-t-primary bg-primary/5'
          : 'border-border border-t-primary bg-card',
        className,
      )}
      style={{ boxShadow: 'var(--shadow-warm-md)' }}
    >
      <div className="space-y-4">
        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
          {title}
        </h2>
        {description ? (
          <p className="mx-auto max-w-2xl text-base sm:text-lg text-muted-foreground">
            {description}
          </p>
        ) : null}
        {actions ? (
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            {actions}
          </div>
        ) : null}
        {footnote ? (
          <p className="mx-auto max-w-xl text-xs text-muted-foreground pt-1">
            {footnote}
          </p>
        ) : null}
      </div>
    </motion.div>
  );
}
